import { useId, type ComponentProps, type ReactNode } from 'react'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '../lib/cn'

/**
 * Form primitives the request forms are built from (frames 4717:8058 and
 * siblings). Each maps to a component in the Figma file rather than being
 * invented here.
 */

/** A titled block: bold heading, grey subtitle, then its fields. */
export function Section({
  title,
  icon,
  required = false,
  subtitle,
  children,
  className,
}: {
  title: string
  /**
   * Drawn before the heading and coloured with it. The document frames put one
   * on every block; passing an element as `title` instead would put it inside
   * the accessible name, where a screen reader announces nothing useful.
   */
  icon?: ReactNode
  required?: boolean
  subtitle?: string
  children: ReactNode
  className?: string
}) {
  const id = useId()
  return (
    <section aria-labelledby={id} className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-1.5">
        <h2
          id={id}
          className={cn(
            'flex items-center gap-2 text-base leading-5 font-bold',
            icon ? 'text-brand-600' : 'text-slate-700',
          )}
        >
          {icon}
          {title}
          {required ? (
            <span className="text-danger" aria-hidden="true">
              {' '}
              *
            </span>
          ) : null}
        </h2>
        {subtitle ? <p className="text-xs text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  )
}

/**
 * A checkbox with its label.
 *
 * A real input, visually hidden, with the box drawn beside it — so the label,
 * the space key, and form participation all work without being re-implemented.
 */
export type CheckboxProps = Omit<ComponentProps<'input'>, 'type'> & { label: ReactNode }

export function Checkbox({ label, className, ...props }: CheckboxProps) {
  return (
    <label className={cn('group relative flex cursor-pointer items-center gap-2', className)}>
      {/* Transparent and covering the whole label rather than `sr-only`: the
          label *is* the control, and a 1px hidden input sits wherever its
          containing block puts it — under its own text, as often as not, where
          nothing aiming at the control can reach it. */}
      <input
        type="checkbox"
        className="peer absolute inset-0 size-full cursor-pointer opacity-0"
        {...props}
      />
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none grid size-[18px] shrink-0 place-items-center rounded-[5px]',
          'border border-slate-400',
          'peer-checked:border-brand-600 peer-checked:bg-brand-600 peer-checked:text-white',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
          'text-transparent',
        )}
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
      <span className="pointer-events-none text-sm font-semibold text-slate-700">{label}</span>
    </label>
  )
}

/**
 * A radio drawn as a bordered tile with a label, an optional hint, and the dot
 * on the right — the design's `Frame 158` / vehicle picker.
 */
export type OptionTileProps = Omit<ComponentProps<'input'>, 'type'> & {
  label: string
  hint?: string
  icon?: ReactNode
}

export function OptionTile({ label, hint, icon, className, ...props }: OptionTileProps) {
  return (
    <label
      className={cn(
        'group relative flex cursor-pointer items-center gap-1.5 rounded-md bg-slate-100 px-3 py-1.5',
        'has-[:checked]:ring-brand-600 has-[:checked]:ring-1',
        'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
        className,
      )}
    >
      {/* Transparent and covering the whole tile, rather than `sr-only`. The
          tile *is* the control — the design gives it no separate hit area — and
          an `sr-only` input is a 1px box wherever its containing block happens
          to put it, which anything drawn over that spot then swallows. Every
          decoration below is `pointer-events-none` so the click always reaches
          the input underneath. */}
      <input
        type="radio"
        className="absolute inset-0 size-full cursor-pointer opacity-0"
        {...props}
      />
      {icon ? <span className="pointer-events-none shrink-0 text-slate-700">{icon}</span> : null}
      <span className="pointer-events-none flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-slate-700">{label}</span>
        {hint ? <span className="truncate text-xs text-slate-600">{hint}</span> : null}
      </span>
      {/* `group-has`, not `peer`: the peer modifier compiles to a sibling
          combinator, which does not reach inside a sibling — and the dot lives
          inside one. */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none grid size-[18px] shrink-0 place-items-center rounded-full',
          'border border-slate-400 bg-white',
          'group-has-[:checked]:border-brand-600',
        )}
      >
        <span className="bg-brand-600 size-2.5 rounded-full opacity-0 group-has-[:checked]:opacity-100" />
      </span>
    </label>
  )
}

/**
 * A number field with increment and decrement controls.
 *
 * The buttons exist because the design draws them, and because typing into a
 * number field on a phone brings up a keyboard to change "1" to "2".
 */
export function NumberStepper({
  label,
  value,
  onValueChange,
  min = 1,
  max = 99,
  id,
}: {
  label: string
  value: number
  onValueChange: (value: number) => void
  min?: number
  max?: number
  id?: string
}) {
  const auto = useId()
  const inputId = id ?? auto
  const clamp = (n: number) => Math.max(min, Math.min(max, n))

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-xs text-slate-600">
        {label}
      </label>
      <div className="flex h-11 items-center rounded-md bg-slate-100 pl-3">
        <input
          id={inputId}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onValueChange(clamp(Number(e.target.value) || min))}
          className="min-w-0 flex-1 [appearance:textfield] bg-transparent text-sm text-slate-900 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <div className="flex flex-col pr-3">
          <button
            type="button"
            onClick={() => onValueChange(clamp(value + 1))}
            aria-label={`Augmenter ${label}`}
            className="text-slate-600 hover:text-slate-900"
          >
            <ChevronUp className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onValueChange(clamp(value - 1))}
            aria-label={`Diminuer ${label}`}
            className="text-slate-600 hover:text-slate-900"
          >
            <ChevronDown className="size-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )
}

/** A number field with a fixed unit printed inside it — "KG", "CFA", "cm". */
export type UnitInputProps = Omit<ComponentProps<'input'>, 'type'> & {
  label?: string
  unit: string
  /** Renders the value large, as the "Mon tarif" field does. */
  emphasis?: boolean
}

export function UnitInput({
  label,
  unit,
  emphasis = false,
  id,
  className,
  ...props
}: UnitInputProps) {
  const auto = useId()
  const inputId = id ?? auto
  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <label htmlFor={inputId} className="text-xs text-slate-600">
          {label}
        </label>
      ) : null}
      <div className="flex h-11 items-center gap-2 rounded-md bg-slate-100 px-3">
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          className={cn(
            'min-w-0 flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none',
            emphasis ? 'text-2xl font-bold' : 'text-sm',
            className,
          )}
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'shrink-0 text-slate-900',
            emphasis ? 'text-2xl font-bold' : 'text-sm font-bold',
          )}
        >
          {unit}
        </span>
      </div>
    </div>
  )
}
